import React, { useState, useRef, useCallback } from 'react'
import type { Variable } from '../types'
import { evaluateFormula } from '../../lib/formula'
import { X } from 'lucide-react'

interface FormulaEditorProps {
  variables: Variable[]
  variableValues: Record<string, number | string | boolean>
  onSubmit: (name: string, formula: string) => void
  onCancel: () => void
  editMode?: boolean
  initialName?: string
  initialFormula?: string
}

export default function FormulaEditor({ variables, variableValues, onSubmit, onCancel, editMode, initialName, initialFormula }: FormulaEditorProps) {
  const [name, setName] = useState(initialName || '')
  const [formula, setFormula] = useState(initialFormula || '')
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [autocompleteFilter, setAutocompleteFilter] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const nonFormulaVars = variables.filter((v) => v.type !== 'formula')
  const allVars = variables

  const filteredVars = autocompleteFilter
    ? allVars.filter((v) => v.name.toLowerCase().startsWith(autocompleteFilter.toLowerCase()))
    : allVars

  const handleFormulaChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setFormula(val)

    // Check if user just typed @
    const cursorPos = e.target.selectionStart ?? val.length
    const textUpToCursor = val.slice(0, cursorPos)
    const atIndex = textUpToCursor.lastIndexOf('@')

    if (atIndex >= 0) {
      const afterAt = textUpToCursor.slice(atIndex + 1)
      if (!/\s/.test(afterAt) && afterAt.length < 30) {
        setAutocompleteFilter(afterAt)
        setShowAutocomplete(true)
        return
      }
    }
    setShowAutocomplete(false)
  }, [])

  const insertVariable = useCallback((varName: string) => {
    const cursorPos = inputRef.current?.selectionStart ?? formula.length
    const textUpToCursor = formula.slice(0, cursorPos)
    const atIndex = textUpToCursor.lastIndexOf('@')

    if (atIndex >= 0) {
      const newFormula = formula.slice(0, atIndex) + '@' + varName + formula.slice(cursorPos)
      setFormula(newFormula)
    }
    setShowAutocomplete(false)
    inputRef.current?.focus()
  }, [formula])

  const preview = formula ? evaluateFormula(formula, variableValues) : null
  const nameError = !editMode && name && variables.some((v) => v.name === name)
  const isValid = name && formula && !nameError

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{editMode ? 'Edit Formula' : 'New Formula'}</span>
        <button onClick={onCancel} className="p-0.5 text-slate-300 hover:text-slate-500">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {editMode ? (
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Name</label>
          <div className="w-full bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-mono text-slate-400">
            @{name}
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            placeholder="total_price"
            className={`w-full bg-white border rounded-lg px-2.5 py-1.5 text-sm font-mono focus:ring-1 focus:ring-[#5048e5] outline-none ${
              nameError ? 'border-red-300' : 'border-slate-200'
            }`}
          />
          {nameError && <p className="text-[10px] text-red-400">Name already used</p>}
        </div>
      )}

      <div className="space-y-1.5 relative">
        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Formula</label>
        <input
          ref={inputRef}
          type="text"
          value={formula}
          onChange={handleFormulaChange}
          placeholder="@price * @quantity + 10"
          className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-mono focus:ring-1 focus:ring-[#5048e5] outline-none"
        />

        {showAutocomplete && filteredVars.length > 0 && (
          <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
            {filteredVars.map((v) => (
              <button
                key={v.name}
                onMouseDown={(e) => { e.preventDefault(); insertVariable(v.name) }}
                className="w-full text-left px-3 py-1.5 text-xs font-mono hover:bg-[#5048e5]/5 transition-colors"
              >
                @{v.name}
                <span className="text-slate-400 ml-2 text-[10px]">{v.type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {preview !== null && (
        <div className="text-xs text-slate-500">
          Preview: <span className="font-mono font-bold text-[#5048e5]">{preview}</span>
        </div>
      )}

      <button
        onClick={() => { if (isValid) onSubmit(name, formula) }}
        disabled={!isValid}
        className={`w-full py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${
          isValid
            ? 'bg-[#5048e5] text-white hover:bg-[#4038c5]'
            : 'bg-slate-100 text-slate-300 cursor-not-allowed'
        }`}
      >
        {editMode ? 'Update Formula' : 'Create Formula'}
      </button>
    </div>
  )
}
